/**
 * Word.js insertion helpers. All calls go through `Word.run` so context sync is
 * handled correctly. Insertions happen at the current selection — if the user
 * has a selection range, that range is replaced; if they have just a cursor, the
 * text is inserted at the cursor.
 *
 * All merge tokens land in the document as `{{...}}` mustache text. Aspose is
 * configured with `MailMerge.UseNonMergeFields = true` (see the backend's
 * `AsposeDocumentGenerationService.ConfigureMailMerge`), which recognises
 * mustache tokens as pseudo-MERGEFIELDs for BOTH scalar merge and
 * `MailMerge.ExecuteWithRegions`. The previous chevron form (`«X»`) was NOT
 * recognised by either mode -- it survived the merge as literal text -- and
 * was the root cause of ticket #2391 (regions not merging in generated PDFs).
 */

// `{{name}}` — the merge-token wrapper Aspose's UseNonMergeFields path
// resolves. Chevrons are display glyphs Word draws for unmerged fields, not
// an accepted syntax on their own.
const tag = (name: string): string => `{{${name}}}`;

export async function insertField(tagName: string): Promise<void> {
  // Curly-brace inline form — matches the {{CF_X}} style the Tag Reference panel
  // (#2368) advertises.
  await insertAtSelection(tag(tagName));
}

export async function insertColumn(columnName: string): Promise<void> {
  // Bare mustache-wrapped column tag — lives inside a {{TableStart:X}} /
  // {{TableEnd:X}} region and is bound to the region's DataTable column of
  // the same name at merge time.
  await insertAtSelection(tag(columnName));
}

export async function insertRegion(groupName: string): Promise<void> {
  // Places the start marker, a blank paragraph for the author to fill, then the
  // end marker. Each on its own line so the author can build a table between
  // them or add repeating text.
  await Word.run(async (ctx) => {
    const range = ctx.document.getSelection();
    range.insertParagraph(tag(`TableStart:${groupName}`), Word.InsertLocation.before);
    range.insertParagraph('', Word.InsertLocation.before);
    range.insertParagraph(tag(`TableEnd:${groupName}`), Word.InsertLocation.before);
    await ctx.sync();
  });
}

export async function insertWholeRegion(groupName: string, columnNames: readonly string[]): Promise<void> {
  // Places the start marker, a paragraph with every column tag space-separated
  // (so the author can turn it into a Word table row), then the end marker.
  // Mirrors the Suite Tag Reference panel's "Whole region" clipboard scaffold.
  const columnsLine = columnNames.map(tag).join(' ');
  await Word.run(async (ctx) => {
    const range = ctx.document.getSelection();
    range.insertParagraph(tag(`TableStart:${groupName}`), Word.InsertLocation.before);
    range.insertParagraph(columnsLine, Word.InsertLocation.before);
    range.insertParagraph(tag(`TableEnd:${groupName}`), Word.InsertLocation.before);
    await ctx.sync();
  });
}

export async function insertTable(
  groupName: string,
  columnNames: readonly string[],
  columnLabels: readonly string[],
): Promise<void> {
  // Inserts a real Word table with a friendly-label header row + one body row
  // wrapped in {{TableStart:X}}/{{TableEnd:X}} markers. Aspose treats the
  // markers in first/last cells as "repeat this row per record" so the
  // rendered document is a proper Word table — one row per record.
  if (columnNames.length === 0) return;
  if (columnLabels.length !== columnNames.length) {
    throw new Error('columnNames and columnLabels must have the same length');
  }
  const headerRow = [...columnLabels];
  const bodyRow = columnNames.map((col, i) => {
    let cell = tag(col);
    if (i === 0) cell = tag(`TableStart:${groupName}`) + cell;
    if (i === columnNames.length - 1) cell = cell + tag(`TableEnd:${groupName}`);
    return cell;
  });
  await Word.run(async (ctx) => {
    const range = ctx.document.getSelection();
    const table = range.insertTable(
      2,
      columnNames.length,
      Word.InsertLocation.before,
      [headerRow, bodyRow],
    );
    // Explicit borders so the table is immediately recognizable in the doc —
    // Word's insert-table default can render as faint gridlines otherwise.
    table.getBorder(Word.BorderLocation.all).type = Word.BorderType.single;
    table.rows.getFirst().font.bold = true;
    await ctx.sync();
  });
}

async function insertAtSelection(text: string): Promise<void> {
  await Word.run(async (ctx) => {
    const range = ctx.document.getSelection();
    range.insertText(text, Word.InsertLocation.replace);
    await ctx.sync();
  });
}
